import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { HelpCircle, ChevronDown, Mail, Phone, Dumbbell, ChevronRight, MessageCircle } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState('0-0');

  useEffect(() => {
    document.title = 'FAQ | Sport Fitt Pro';
  }, []);

  const faqs = [
    {
      category: 'Orders & Shipping',
      questions: [
        { q: 'How long does shipping take?', a: 'Standard shipping within the USA takes 3-7 business days. We currently only ship within the United States.' },
        { q: 'Is shipping really free?', a: 'Yes! We offer free standard shipping on all orders over $75 within the USA. Orders under $75 have a flat shipping rate of $9.95.' },
        { q: 'Can I track my order?', a: 'Absolutely! Once your order ships, you\'ll receive a tracking number via email to monitor delivery progress.' },
        { q: 'Do you ship internationally?', a: 'Currently we only ship within the United States. We\'re working on expanding to more countries soon!' },
      ]
    },
    {
      category: 'Returns & Refunds',
      questions: [
        { q: 'What is your return policy?', a: 'We offer a 30-day return policy. Items must be unused and in original packaging. Full refund within 5-7 business days.' },
        { q: 'How do I return an item?', a: 'Contact us first to request a return authorization. Then ship the item to our return address in Bolingbrook, IL.' },
        { q: 'Who pays for return shipping?', a: 'Return shipping is the customer\'s responsibility unless the item was defective or we shipped the wrong item.' },
      ]
    },
    {
      category: 'Products',
      questions: [
        { q: 'Are your products authentic?', a: 'Yes, all products on Sport Fitt Pro are 100% authentic and sourced directly from authorized suppliers.' },
        { q: 'Do you offer product warranties?', a: 'Many products come with manufacturer warranties. Contact us for specific warranty information on any product.' },
        { q: 'Can I get product recommendations?', a: 'Yes! Our team is happy to help you find the right gear for your training goals. Contact us with your questions.' },
      ]
    },
    {
      category: 'Payment & Security',
      questions: [
        { q: 'What payment methods do you accept?', a: 'We accept Visa, Mastercard, American Express, Discover and other secure payment methods at checkout.' },
        { q: 'Is my payment information secure?', a: 'Yes! We use SSL encryption and secure payment processing. We never store your full credit card details.' },
        { q: 'How will the charge appear on my statement?', a: 'Charges will appear as "SPORT FITT PRO" on your credit card statement.' },
      ]
    }
  ];

  const toggleQ = (i, j) => {
    const k = `${i}-${j}`;
    setOpenIndex(openIndex === k ? null : k);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-72 overflow-hidden bg-ink-950">
          <img src={siteImages.heroMain} alt="FAQ" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/80 to-transparent" />
          <div className="absolute bottom-0 right-0 w-2/5 h-2 bg-athletic-500" />
          <div className="relative h-full max-w-7xl mx-auto px-4 lg:px-8 flex items-end pb-10">
            <div>
              <nav className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase tracking-wider mb-4">
                <Link to="/" className="hover:text-athletic-400 transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-white">FAQ</span>
              </nav>
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Help Center</p>
              <h1 className="font-display text-5xl md:text-6xl text-white leading-none tracking-wider">
                FREQUENTLY <span className="text-athletic-500">ASKED</span>
              </h1>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16">
          <div className="space-y-10">
            {faqs.map((cat, ci) => (
              <div key={ci}>
                <h2 className="font-display text-3xl text-ink-900 tracking-wider mb-5 flex items-center gap-3">
                  <span className="w-10 h-10 bg-athletic-500 flex items-center justify-center">
                    <Dumbbell className="w-5 h-5 text-white" />
                  </span>
                  {cat.category.toUpperCase()}
                </h2>
                <div className="space-y-3">
                  {cat.questions.map((faq, qi) => {
                    const k = `${ci}-${qi}`;
                    const open = openIndex === k;
                    return (
                      <div key={qi} className={`bg-white border-2 ${open ? 'border-athletic-500' : 'border-ink-100'} transition-colors overflow-hidden`}>
                        <button onClick={() => toggleQ(ci, qi)} className="w-full flex items-center justify-between p-5 text-left hover:bg-ink-50 transition-colors">
                          <span className="font-bold text-ink-900 pr-4">{faq.q}</span>
                          <ChevronDown className={`w-5 h-5 text-athletic-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </button>
                        {open && (
                          <div className="px-5 pb-5 pt-0">
                            <div className="pl-0 border-l-4 border-athletic-500 pl-4">
                              <p className="text-ink-600 leading-relaxed">{faq.a}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Still Have Questions CTA */}
          <div className="mt-14 bg-ink-950 text-white p-10 text-center relative overflow-hidden border-l-8 border-athletic-500">
            <div className="relative">
              <MessageCircle className="w-10 h-10 text-athletic-400 mx-auto mb-4" />
              <h2 className="font-display text-3xl tracking-wider mb-3">STILL HAVE QUESTIONS?</h2>
              <p className="text-white/70 mb-6 max-w-md mx-auto">
                Real humans ready to help. Reach out and we'll reply within 24 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`mailto:${CONTACT_INFO.email}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider text-xs transition-colors">
                  <Mail className="w-4 h-4" /> Email Us
                </a>
                <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-ink-950 hover:bg-volt-400 font-black uppercase tracking-wider text-xs transition-colors">
                  <Phone className="w-4 h-4" /> {CONTACT_INFO.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
